import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { host } from "../../config/Config";
import "../style/styles.css";
import { Checkbox } from "@mui/material";

const translationMap = {
    "DODATNO PRANJE KOSE": "EXTRA HAIR WASH",
    "SKIDANJE BRADE": "BEARD REMOVAL",
    "BRADA CLASSIC SREDNJA": "BEARD CLASSIC MID",
    "BRADA CLASSIC DUGAČKA": "BEARD CLASSIC LONG",
    "CLASSIC NULERICA": "CLASSIC ZERO",
    "CLASSIC KRATKA KOSA": "CLASSIC SHORT HAIR",
    "CLASSIC DUŽA KOSA": "CLASSIC LONG HAIR",
};

const translations = {
    20: {
        "Datum Termina": "Appointment Date",
        "Vreme Termina": "Appointment Time",
        "Vrsta Usluge": "Service Type",
        "Lični podaci": "Personal Information",
        "Rezerviši termin": "Book Appointment",
    },
    10: {
        "Datum Termina": "Datum Termina",
        "Vreme Termina": "Vreme Termina",
        "Vrsta Usluge": "Vrsta Usluge",
        "Lični podaci": "Lični podaci",
        "Rezerviši termin": "Rezerviši termin",
    },
};

function formatToLocalDateTime(date, time) {
    const [day, month, year] = date.split(".");
    const [hours, minutes] = time.split(":");

    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
}

const getTranslation = (label, language) => {
    return translations[language]?.[label] || label;
};

export default function VerticalLinearStepper() {
    const [checkedItems, setCheckedItems] = React.useState({});
    const [haircutData, setHaircutData] = React.useState([]);
    const [dateTimeData, setDateTimeData] = React.useState([]);
    const [activeStep, setActiveStep] = React.useState(0);
    const [selections, setSelections] = React.useState({
        0: null,
        1: null,
        2: null,
        3: null,
        4: null,
    });
    const [inputValues, setInputValues] = React.useState({
        field1: "",
        field2: "",
        field3: "",
    });
    const [visibleCount, setVisibleCount] = React.useState(7);
    const targetRef = React.useRef(null);
    const [language, setLanguage] = React.useState("10"); // Default to "10" (Srpski)

    const navigate = useNavigate();
    const location = useLocation();

    const steps = [
        { label: getTranslation("Datum Termina", language) },
        { label: getTranslation("Vreme Termina", language) },
        { label: getTranslation("Vrsta Usluge", language) },
        { label: getTranslation("Lični podaci", language) },
        { label: getTranslation("Rezerviši termin", language) },
    ];

    const handleScroll = () => {
        // Scroll to the target element
        if (targetRef.current) {
            targetRef.current.scrollIntoView({
                behavior: "smooth", // Smooth scrolling
                block: "start", // Align element to the top of the view
            });
        }
    };

    async function scheduleReservation(
        startTime,
        endTime,
        haircut,
        client,
        phone,
        email = null
    ) {
        const url = `${host}/api/v1/reservations`;

        const payload = {
            startTime,
            endTime,
            haircut,
            client,
            phone,
            email,
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    `Error: ${
                        error.message || "Failed to schedule appointment"
                    }`
                );
            }

            const data = await response.json();
            return data;
        } catch (error) {
            navigate("/reservation-response", {
                state: {
                    date: null,
                    time: null,
                    haircut: null,
                    client: null,
                    phone: null,
                    email: null,
                    successfull: false,
                },
            });
        }
    }

    React.useEffect(() => {
        const loadUserData = () => {
            const tokenName = localStorage.getItem("zajednica-name");
            const tokenPhone = localStorage.getItem("zajednica-phone");
            const tokenEmail = localStorage.getItem("zajednica-email");

            setInputValues({
                field1: tokenName || "",
                field2: tokenPhone || "",
                field3: tokenEmail || "",
            });
        };

        loadUserData();

        const storedLanguage = localStorage.getItem("selectedLanguage");
        if (storedLanguage) {
            setLanguage(storedLanguage);
        }

        const handleStorageChange = () => {
            setLanguage(localStorage.getItem("selectedLanguage") || "10");
            loadUserData(); // Reload user data when localStorage changes
        };

        window.addEventListener("storage", handleStorageChange);

        // Also listen for custom event when email is saved in same tab
        const handleEmailUpdate = () => {
            loadUserData();
        };
        window.addEventListener("emailUpdated", handleEmailUpdate);

        let isMounted = true;
        const { appointmentsData } = location.state || {};
        setDateTimeData(appointmentsData);

        fetch(`${host}/api/v1/haircuts`)
            .then((response) => response.json())
            .then((data) => {
                if (isMounted) {
                    const formattedData = data.map(
                        (item) => `${item.name} - ${item.price} RSD`
                    );
                    setHaircutData(formattedData);
                }
            })
            .catch((error) => console.error("Error fetching prices:", error));

        return () => {
            isMounted = false;
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("emailUpdated", handleEmailUpdate);
        };
    }, []);

    const HandleReservation = async () => {
        if (
            !selections[0] ||
            !selections[1] ||
            !selections[2] ||
            !inputValues.field1 ||
            !inputValues.field2
        ) {
            return;
        }

        let haircuts = "";
        selections[2].forEach((haircut) => {
            haircuts += haircut + "#";
        });

        let date = selections[0];
        let startTime = selections[1]?.split("-")[0].trim();
        let endTime = selections[1]?.split("-")[1].trim();

        const formattedStartTime = formatToLocalDateTime(date, startTime);
        const formattedEndTime = formatToLocalDateTime(date, endTime);

        const response = await scheduleReservation(
            formattedStartTime,
            formattedEndTime,
            haircuts,
            inputValues.field1,
            inputValues.field2,
            inputValues.field3
        );

        if (!response) return;

        navigate("/reservation-response", {
            state: {
                date: selections[0],
                time: selections[1],
                haircut: haircuts,
                client: inputValues.field1,
                phone: inputValues.field2,
                email: inputValues.field3,
                successfull: response.id,
            },
        });
    };

    const handleStepClick = (stepIndex) => {
        setActiveStep((prevStep) => (prevStep === stepIndex ? -1 : stepIndex));
    };

    const handleItemClick = (stepIndex, item) => {
        if (selections[stepIndex] === item) {
            if (stepIndex !== 2) {
                setActiveStep(stepIndex);
                return;
            }
        }

        setCheckedItems((prevCheckedItems) => {
            const updatedCheckedItems = { ...prevCheckedItems };

            if (!updatedCheckedItems[stepIndex]) {
                updatedCheckedItems[stepIndex] = [];
            }

            if (updatedCheckedItems[stepIndex].includes(item)) {
                updatedCheckedItems[stepIndex] = updatedCheckedItems[
                    stepIndex
                ].filter((selectedItem) => selectedItem !== item);
            } else {
                updatedCheckedItems[stepIndex] = [
                    ...updatedCheckedItems[stepIndex],
                    item,
                ];
            }

            return updatedCheckedItems;
        });

        setSelections((prevSelections) => {
            let updatedSelections;
            if (stepIndex === 2) {
                updatedSelections = {
                    ...prevSelections,
                    [stepIndex]: prevSelections[stepIndex]?.includes(item)
                        ? prevSelections[stepIndex].filter(
                              (selectedItem) => selectedItem !== item
                          )
                        : [...(prevSelections[stepIndex] || []), item],
                };

                if (updatedSelections[stepIndex].length === 0) {
                    updatedSelections[stepIndex] = null;
                }
            } else {
                updatedSelections = {
                    ...prevSelections,
                    [stepIndex]:
                        prevSelections[stepIndex] === item ? null : item,
                };
            }

            if (stepIndex === 0) {
                updatedSelections[1] = null;

                if (inputValues.field1.trim() && inputValues.field2.trim()) {
                    updatedSelections[3] = true;
                } else {
                    updatedSelections[3] = null;
                }

                setActiveStep(1);
                return updatedSelections;
            }

            return updatedSelections;
        });

        const nextIncompleteStep = Object.keys(selections).find(
            (key) => !selections[key] && parseInt(key) > stepIndex
        );

        if (nextIncompleteStep !== undefined) {
            if (stepIndex === 2) return;
            setActiveStep(parseInt(nextIncompleteStep));
        } else {
            setActiveStep(steps.length - 1);
        }
    };
    const handleInputChange = (field, value) => {
        setInputValues((prev) => {
            const updatedValues = {
                ...prev,
                [field]: value,
            };

            if (updatedValues.field1.trim() && updatedValues.field2.trim()) {
                setSelections((prevSelections) => ({
                    ...prevSelections,
                    3: true,
                }));
            } else {
                setSelections((prevSelections) => ({
                    ...prevSelections,
                    3: null,
                }));
            }

            return updatedValues;
        });
    };

    const pickBasedOnIndex = (index) => {
        let items = [];
        let message = null;

        if (index === 0) {
            items = Object.keys(dateTimeData)
                .map((date) => {
                    const [day, month, year] = date.split(".");
                    return new Date(`${year}-${month}-${day}`);
                })
                .sort((a, b) => a - b)
                .map((date) => {
                    return `${
                        date.getDate() < 10
                            ? "0" + date.getDate()
                            : date.getDate()
                    }.${
                        date.getMonth() + 1 < 10
                            ? "0" + (date.getMonth() + 1)
                            : date.getMonth() + 1
                    }.${date.getFullYear()}.`;
                });

            return (
                <div className='barber-zajednica-date-picker-list'>
                    {message && (
                        <Typography
                            color='error'
                            fontFamily='Bebas Neue; sans-serif'
                            fontSize='23px'
                        >
                            {message}
                        </Typography>
                    )}
                    {items.slice(0, visibleCount).map((item, idx) => (
                        <div
                            key={item}
                            className={`barber-zajednica-date-picker-list-item ${
                                selections[index] === item ? "selected" : ""
                            }`}
                            style={{
                                height: item.includes("RSD") ? "65px" : "45px",
                            }}
                            onClick={() => handleItemClick(index, item)}
                        >
                            <h1>{item}</h1>
                        </div>
                    ))}
                    {visibleCount < items.length && (
                        <button
                            style={{
                                width: "80%",
                                height: "40px",
                                border: "2px solid #f3f3f3",
                                backgroundColor: "#be9455",
                                borderRadius: "10px",
                                fontFamily: "Bebas Neue, sans-serif",
                                color: "white",
                                fontSize: "24px",
                                marginTop: "30px",
                            }}
                            onClick={() =>
                                setVisibleCount((prev) =>
                                    prev + 7 >= items.length
                                        ? items.length
                                        : prev + 7
                                )
                            }
                        >
                            {language == 20 ? "Show More" : "Prikaži još"}
                        </button>
                    )}
                </div>
            );
        } else if (index === 1) {
            if (!selections[0]) {
                message =
                    language == 20
                        ? "Pick appointment date first"
                        : "Prvo odaberite datum termina";
            } else {
                items = dateTimeData[selections[0]] || [];
            }
        } else if (index === 2) {
            items = haircutData;

            return (
                <div className='barber-zajednica-date-picker-list'>
                    {message && (
                        <Typography
                            color='error'
                            fontFamily='Bebas Neue; sans-serif'
                            fontSize='23px'
                        >
                            {message}
                        </Typography>
                    )}
                    {items.map((item) => (
                        <div
                            key={item}
                            className={`barber-zajednica-date-picker-list-item-haircut ${
                                selections[index] === item ? "selected" : ""
                            }`}
                            style={{
                                height: item.includes("RSD") ? "65px" : "45px",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(index, item);
                            }}
                        >
                            <div className='barber-zajednica-date-picker-list-item-bigger'>
                                <div>
                                    <Checkbox
                                        checked={
                                            checkedItems[index]?.includes(
                                                item
                                            ) || false
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleItemClick(index, item);
                                        }}
                                        sx={{
                                            paddingTop: "15px",
                                            color: "#be9455",
                                            "&.Mui-checked": {
                                                color: "#be9455",
                                            },
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        paddingLeft: "20px",
                                    }}
                                >
                                    <h1>
                                        {language == 20
                                            ? translationMap[
                                                  item.split("-")[0].trim()
                                              ] || item.split("-")[0]
                                            : item.split("-")[0]}
                                    </h1>
                                    <h1 style={{ width: "auto " }}>
                                        {item.split("-")[1]}
                                    </h1>
                                </div>
                            </div>
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeStep < steps.length - 1) {
                                        handleStepClick(activeStep + 1, items);
                                    }
                                }}
                                style={{
                                    zIndex: 100,
                                    height: "70px",
                                    position: "fixed",
                                    bottom: "30px",
                                    borderRadius: "15px",
                                    width: "85%",
                                    left: "30px",
                                    backgroundColor: "#be9455",
                                    color: "white",
                                    border: "2px solid white",
                                    textAlign: "center",
                                    padding: "10px 0",
                                    fontFamily: "Bebas Neue, sans-serif",
                                    fontSize: "34px",
                                }}
                            >
                                {language == 20 ? "Next Step" : "Sledeći korak"}
                            </div>
                        </div>
                    ))}
                </div>
            );
        } else if (index === 3) {
            return (
                <div className='barber-zajednica-input-fields'>
                    <input
                        type='text'
                        placeholder={
                            language == 20
                                ? "Name and Surname"
                                : "Ime i Prezime"
                        }
                        value={inputValues.field1}
                        onChange={(e) =>
                            handleInputChange("field1", e.target.value)
                        }
                        className='barber-input'
                    />
                    <input
                        type='number'
                        placeholder={
                            language == 20
                                ? "Phone number"
                                : "Telefon ( 06* *** *** )"
                        }
                        value={inputValues.field2}
                        onChange={(e) =>
                            handleInputChange("field2", e.target.value)
                        }
                        className='barber-input'
                    />
                    <input
                        type='text'
                        placeholder={
                            language == 20
                                ? "Email ( Optional )"
                                : "Email ( Opciono )"
                        }
                        value={inputValues.field3}
                        onChange={(e) =>
                            handleInputChange("field3", e.target.value)
                        }
                        className='barber-input'
                    />
                    <button
                        className='barber-input-button'
                        onClick={() => {
                            if (activeStep < steps.length - 1) {
                                handleStepClick(activeStep + 1, items);
                            }
                        }}
                    >
                        {language == 20 ? "Next Step" : "Sledeći korak"}
                    </button>
                </div>
            );
        } else if (index === 4) {
            return (
                <div className='barber-zajednica-reserve-appointment'>
                    <div>
                        <div
                            style={{
                                color: selections[0]
                                    ? "#f3f3f3"
                                    : "rgb(122, 23, 23)",
                            }}
                        >
                            <span className='barber-zajednica-reserve-appointment-item'>
                                {language == 20 ? "Date: " : "Datum: "}{" "}
                            </span>
                            {selections[0] ||
                                (language == 20
                                    ? "Choose Date"
                                    : "Odaberite datum")}
                        </div>
                        <div
                            style={{
                                color: selections[1]
                                    ? "#f3f3f3"
                                    : "rgb(122, 23, 23)",
                            }}
                        >
                            <span className='barber-zajednica-reserve-appointment-item'>
                                {language == 20 ? "Time: " : "Vreme: "}{" "}
                            </span>
                            {selections[1] ||
                                (language == 20
                                    ? "Choose Time"
                                    : "Odaberite vreme")}
                        </div>

                        <div>
                            {selections[2]?.length > 0 ? (
                                selections[2].map((service, index) => (
                                    <div
                                        key={index}
                                        style={{ color: "#f3f3f3" }}
                                    >
                                        <span className='barber-zajednica-reserve-appointment-item'>
                                            {language == 20
                                                ? "Service: "
                                                : "Usluga : "}{" "}
                                        </span>
                                        {language == 20
                                            ? translationMap[
                                                  service.split("-")[0].trim()
                                              ] || service.split("-")[0]
                                            : service.split("-")[0]}
                                    </div>
                                ))
                            ) : (
                                <div style={{ display: "flex" }}>
                                    <div style={{ color: "#f3f3f3" }}>
                                        <span className='barber-zajednica-reserve-appointment-item'>
                                            {language == 20
                                                ? "Service: "
                                                : "Usluga : "}{" "}
                                        </span>
                                    </div>
                                    <div style={{ color: "rgb(122, 23, 23)" }}>
                                        {language == 20
                                            ? "  Pick a Service"
                                            : " Odaberite Uslugu"}{" "}
                                    </div>
                                </div>
                            )}
                        </div>

                        {selections[2]?.length > 0 && (
                            <div style={{ color: "#f3f3f3" }}>
                                <span className='barber-zajednica-reserve-appointment-item'>
                                    {language == 20
                                        ? "Total price: "
                                        : "Ukupna cena: "}
                                </span>
                                {selections[2]
                                    .map((service) =>
                                        parseFloat(
                                            service
                                                .split(" - ")[1]
                                                .replace("RSD", "")
                                                .trim()
                                        )
                                    )
                                    .reduce(
                                        (total, price) => total + price,
                                        0
                                    )}{" "}
                                RSD
                            </div>
                        )}

                        <div
                            style={{
                                color: inputValues.field1
                                    ? "#f3f3f3"
                                    : "rgb(122, 23, 23)",
                            }}
                        >
                            <span className='barber-zajednica-reserve-appointment-item'>
                                {language == 20 ? "Client: " : "Klijent :"}{" "}
                            </span>
                            {inputValues.field1
                                ? `${inputValues.field1}`
                                : language == 20
                                ? "Provide Personal Info"
                                : "Unesite Podatke"}
                        </div>
                        <div
                            style={{
                                color: inputValues.field2
                                    ? "#f3f3f3"
                                    : "rgb(122, 23, 23)",
                            }}
                        >
                            <span className='barber-zajednica-reserve-appointment-item'>
                                {language == 20 ? "Phone: " : "Telefon :"}{" "}
                            </span>
                            {inputValues.field2
                                ? `${inputValues.field2}`
                                : language == 20
                                ? "Provide Phone Number"
                                : "Unesite Telefon"}
                        </div>
                        {inputValues.field3 && (
                            <div
                                style={{
                                    maxHeight: "70px",
                                    width: "110%",
                                    overflow: "auto",
                                }}
                            >
                                <span className='barber-zajednica-reserve-appointment-item'>
                                    {language == 20
                                        ? "Email address : "
                                        : "Email Adresa :"}{" "}
                                </span>
                                {inputValues.field3
                                    ? `${inputValues.field3}`
                                    : "/"}
                            </div>
                        )}
                    </div>
                    <button
                        className='barber-zajednica-reserve-appointment-finish'
                        onClick={HandleReservation}
                    >
                        {language == 20
                            ? "Reserve Appointment"
                            : "Rezerviši termin"}
                    </button>
                </div>
            );
        }

        return (
            <div className='barber-zajednica-date-picker-list'>
                {message && (
                    <Typography
                        color='error'
                        fontFamily='Bebas Neue; sans-serif'
                        fontSize='23px'
                    >
                        {message}
                    </Typography>
                )}
                {items.map((item) => (
                    <div
                        key={item}
                        className={`barber-zajednica-date-picker-list-item ${
                            selections[index] === item ? "selected" : ""
                        }`}
                        style={{
                            height: item.includes("RSD") ? "65px" : "45px",
                        }}
                        onClick={() => handleItemClick(index, item)}
                    >
                        <h1>{item}</h1>
                    </div>
                ))}
            </div>
        );
    };

    const getStepIcon = (index) => {
        const { field1, field2, field3 } = inputValues;
        const allCompleted =
            selections[0] &&
            selections[1] &&
            selections[2] &&
            field1.trim() &&
            field2.trim();

        if (index === 3) {
            // Step 3 requires both fields to be filled in
            if (field1.trim() && field2.trim()) {
                return <CheckCircle style={{ color: "#be9455" }} />;
            } else {
                return <Cancel style={{ color: "#be9455" }} />;
            }
        }

        if (index === 4) {
            // Step 4 should only show completed if all prior steps are done
            return allCompleted ? (
                <CheckCircle style={{ color: "#be9455" }} />
            ) : (
                <Cancel style={{ color: "#be9455" }} />
            );
        }

        // For steps 0, 1, and 2, just check if there's a selection
        return selections[index] ? (
            <CheckCircle style={{ color: "#be9455" }} />
        ) : (
            <Cancel style={{ color: "#be9455" }} />
        );
    };

    const getStepLabelClass = (index) => {
        if (index === 3) {
            const { field1, field2 } = inputValues;
            if (field1.trim() && field2.trim()) {
                return "completed-step-label";
            }
            return "expanded";
        }
        return selections[index] ? "completed-step-label" : "";
    };

    return (
        <Box
            style={{ width: "100%", paddingLeft: "5%" }}
            sx={{ maxWidth: 1000 }}
        >
            <Stepper activeStep={activeStep} orientation='vertical'>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel
                            className={`barber-zajednica-stepper-step
                ${selections[index] ? "selected" : ""}
                ${activeStep === index ? "active" : ""}
                ${activeStep >= index ? "expanded" : ""}
                ${getStepLabelClass(index)}
                ${selections[index] ? "selected-title" : ""}`}
                            onClick={() => handleStepClick(index)}
                            icon={getStepIcon(index)}
                        >
                            {step.label}
                        </StepLabel>

                        <StepContent>{pickBasedOnIndex(index)}</StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
}
